import React from 'react';
import { Helmet } from 'react-helmet';

import { LocalCache } from '../../core/src/local-storage-cache';
import { Helpers } from '../../core/src/helpers';
import ReorderProduct from './reorder-product';
import { withError } from '../../shared/components/error-boundary';
import Link from '../../shared/components/hyperlink';

import '../css/reorder.less';

const localCache = new LocalCache('msgai-reorder', 90);


const PAGE_TYPES = {
	REORDER: 'reorder',
	LOI: 'loi'
}

class Reorder extends React.Component {

    constructor(props) {
        super(props);
        //initialize re-order for SSR
        if(Object.keys(props.reorder).length > 0){
            this.state = this.getNewReorderState();
        }else{
            this.state = {
                prevOrderDate: null,
                reorderProducts: [],
                relatedProducts: [],
                prdPurchaseMap: {},
				prdSkus:[],
				printerId: [],
				pageType: ''
            };
        }
    }

    componentDidMount() {
         const parsedFilters = this.parseFilters(this.props.location.search.replace("?","")) || { query: {} };
		//fallback for CSR when reorder not pre-loaded into redux store
		let { query={} } = parsedFilters;
        const skus = query.sku && query.sku[0].split(',') || [];
        //only retry the api call if there are skus in the query
        if(Object.keys(this.props.reorder).length === 0 && skus.length > 0){
            this.props.fetchReorderProducts(skus, query.printerID, query.source, query.orderDate);
        }else{
			//trigger SSR pageView
            this.props.receiveReorderInfo(this.props.reorder, this.props.primarySku, query.source, query.orderDate);
        }

        localCache.set("linkId",parsedFilters.query.linkId[0]);
    }

    componentDidUpdate(prevProps){
        if(prevProps.reorder !== this.props.reorder){
            this.setNewOrderState();
        }
        else if(prevProps.cartInfo !== this.props.cartInfo){
            const prdPurchaseMap = this.getUpdatedProductMap();
            this.handleChange({ prdPurchaseMap });
        }
    }

    componentWillUnmount(){
        this.props.clearReorderInfo();
    }

    parseFilters = Helpers.memoize(
        (filters) => Helpers.parseFilters(filters)
    );

    setNewOrderState = () => {
        this.handleChange(this.getNewReorderState());
    }

    getNewReorderState = () => {
        
        const parsedFilters = this.parseFilters(this.props.location.search);
		const { query={} } = parsedFilters;
        const skuList = query.sku && query.sku[0].split(",");
        const skuParamMap =  query.quantity && query.quantity[0].split(',').reduce((r,item,i)=> {
            try{
				let sku = decodeURIComponent(skuList[i]);
                r[sku] = { quantity: parseInt(item), index: i }
            }catch(e){}
            return r;
		},{}) || {};
        const prevOrderDate = this.getPrevOrderDate(parsedFilters);
		const extractedProducts = this.extractProducts(this.props.reorder, skuParamMap);
		const prdPurchaseMap = this.getUpdatedProductMap(extractedProducts.prdSkus);
		const pageType = this.getPageType(parsedFilters.query.source);
		const printerId = query.printerID;
        return {printerId, pageType, prevOrderDate, prdPurchaseMap, ...extractedProducts};
    }

    extractProducts = (reorderProducts, skuParamMap) => {
        return Object.keys(reorderProducts).reduce((result, prdKey, i)=>{

            result.reorderProducts.push(...reorderProducts[prdKey].products.map((p) => {
                //set qty for previously ordered products
                p.quantity = (skuParamMap[p.sku] || {}).quantity || 1;
                return p;
            }));
            result.relatedProducts.push(...reorderProducts[prdKey].retlatedProducts);
            this.getProductSkus(reorderProducts[prdKey], result.prdSkus);
            return result;
        },{ reorderProducts: [], relatedProducts: [], prdSkus: [] });
    }

    getProductSkus = (data, arr) => {
        data.products.forEach((prd)=>{
            arr.push(prd.sku);
        })
        data.retlatedProducts.forEach((prd)=>{
            arr.push(prd.sku);
        })
    }

    getUpdatedProductMap = (prdSkus=this.state.prdSkus) => {
        const prdPurchaseMap = this.populateProductMap(prdSkus);
        const { items=[] } = this.props.cartInfo;
        return items.reduce((newMap, crtItem) => {
            prdPurchaseMap[crtItem.pNum] = crtItem;
            return newMap;
        }, prdPurchaseMap);
    }

    populateProductMap = (prdSkus) => {
        return prdSkus.reduce((hashMap, sku)=>{
            if(!(sku in hashMap)) hashMap[sku] = null;
            return hashMap;
        },{});
    }



    getPrevOrderDate = (parsedFilters) => {
        const parsedOrderDate = Number(parsedFilters.query.orderDate);
        if(isNaN(parsedOrderDate)){
            return null;
        }
        return Helpers.date.toDateString(new Date(parsedOrderDate));
    }

    shippingBannerCheck = (product) => {
        const { attributes } = product;
        const subCat = Helpers.getMappedSubCat(attributes.plcode);
        if((subCat === "toner" || subCat === "ink") && !attributes.pm_product.match(/^.*(Instant Ink).*$/gi)){
            return true;
        }
        return false;
	}
	
	getPageType = (source) => {
		return source && source[0].split('|').includes("loi") ? PAGE_TYPES.LOI : PAGE_TYPES.REORDER;
	}

    handleChange = (stateSlice) => {
        this.setState(stateSlice);
    };


    render() {
        const { reorderProducts, relatedProducts, prevOrderDate, prdPurchaseMap, printerId, pageType } = this.state;
        const { cartInfo, primarySku } = this.props;

        const helmetImageUrl = reorderProducts.length > 0 ? Helpers.getCDNUrl(reorderProducts[0].images[0].cdn, reorderProducts[0].images[0].lrg,300) : '';
        const helmetSeries = reorderProducts.length > 0 ? reorderProducts[0].attributes.pm_series : '';
        const primarySkuName = (primarySku && primarySku.name) || printerId;
        const titleText = primarySkuName && pageType === PAGE_TYPES.LOI ? 
                            `Compatible cartridges for ${primarySkuName} : ` : 
                            prevOrderDate ? `You previously ordered on ${prevOrderDate}:` :
                            null;
                            
        return (
            <div className="reorder-container">
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>Ink & Toner</title>
                    <meta property="og:image" content={helmetImageUrl} />
                    <meta property="og:title" content={helmetSeries} />
                    <meta property="og:description" content="store.hp.com" />
                </Helmet>

                {!!titleText && <h1 className="reorder-header">{titleText}</h1>}
                {
                    reorderProducts.map((prd, i) => {
                        const isFreeShipping = this.shippingBannerCheck(prd);
                        return (
                            <ReorderProduct
                                key={`reorder-product-${i}`}
                                className={pageType !== PAGE_TYPES.LOI ? 'reorder-prd' : 'relative-prd'}
                                product={prd}
                                isFreeShipping={isFreeShipping}
                                isReorderProduct={pageType !== PAGE_TYPES.LOI}
                                prdCartRecord={prdPurchaseMap[prd.sku]}
                            />
                        )
                    })
                }
                {
                    pageType !== PAGE_TYPES.LOI && relatedProducts.length > 0 && (
                        <h1 className="reorder-header">You may also need:</h1>
                    )
                }
                {
                    relatedProducts.map((prd, i) => {
                        const isFreeShipping = this.shippingBannerCheck(prd);
                        return (
                            <ReorderProduct
                                key={`relative-product-${i}`}
                                className="relative-prd"
                                product={prd}
                                isFreeShipping={isFreeShipping}
                                isReorderProduct={false}
                                isPurchased={prdPurchaseMap[prd.sku]}
                                purchaseCallBack={this.purchaseCallBack}
                            />
                        )
                    })
                }
                {cartInfo.tQty > 0 && (
                    <div className="sticky-back-to-cart fadein">
                        <Link to={Helpers.decodeHtml(cartInfo.crtUrl)} linkType="absolute">
                            <h3>Go to checkout</h3>
                            <p>
                                ({cartInfo.tQty} item{cartInfo.tQty !== 1 && 's'} in cart)
                            </p>
                        </Link>
                    </div>
                )}
            </div>
        );
    }
}

export default withError(Reorder);
